import { ReactNode, useCallback, useState } from "react";

import { useSyncRef } from "hooks/useSyncRef";
import versions from "versions.json";

import { AppVersionContext, AppVersionState } from "./AppVersionContext";

// WARNING:
// This component do not really track available versions anymore.
// It is left here so we don't need to change the rest of the (old) codebase.
export const AppVersionProvider = ({ children }: { children: ReactNode }) => {
  const [minimumCodeVersion, setMinimumCodeVersion] = useState(0);
  const minimumCodeVersionRef = useSyncRef(minimumCodeVersion);
  const updateMinimumCodeVersionIfNeeded = useCallback(
    (version: number) => {
      if (version <= minimumCodeVersionRef.current) return;
      setMinimumCodeVersion(version);
    },
    [minimumCodeVersionRef],
  );
  const [availableCodeVersion] = useState<number>();
  const currentCodeVersion = versions.code_version;

  const state: AppVersionState = (() => {
    // No API call made yet
    if (!minimumCodeVersion) return "NONE";
    // Technically complexe: we got an API call before getAvailableCodeVersion, just wait for the json fetch to end
    if (!availableCodeVersion) return "NONE";

    if (currentCodeVersion < minimumCodeVersion) {
      // We are outdated
      return availableCodeVersion < minimumCodeVersion
        ? // There is no satisfactory version out yet.
          // This is the case when the backend deployed but the frontend is still deploying.
          "RELEASING"
        : // Compatible version is available
        currentCodeVersion < minimumCodeVersion
        ? "OUTDATED" // This is breaking change
        : "UNKNOWN_ENUM_USED"; // Some data is not correctly handle
    }

    // The code we are running is sufficiently up-to-date, but we hint to update if possible
    return currentCodeVersion === availableCodeVersion
      ? "NONE" // Running last version
      : "UPDATE_AVAILABLE";
  })();

  return (
    <AppVersionContext.Provider
      value={{
        state,
        minimumCodeVersion,
        updateMinimumCodeVersionIfNeeded,
        downloadingUpdate: false,
        availableCodeVersion,
        reload: () => {
          window.location.reload();
        },
        downloadUpdateIfOutdated: () => {
          // No-op: native updates are no longer supported
        },
      }}
    >
      {children}
    </AppVersionContext.Provider>
  );
};
